import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, Tag } from "@/components/badges";
import EntryDetailDialog from "@/components/EntryDetailDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  MagnifyingGlass, FilePdf, LinkSimple, ArrowUp, ArrowDown, Gavel,
  BookmarkSimple, Database, Archive, Trash, X, DownloadSimple,
} from "@phosphor-icons/react";

const ROUTE_ACTIONS = [
  { key: "editorial", label: "Editorial Folder", icon: BookmarkSimple },
  { key: "working", label: "Reference DB", icon: Database },
  { key: "archive", label: "Archive", icon: Archive },
];

export default function EntriesList({ status, title, subtitle }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [detailId, setDetailId] = useState(null);
  const isEditor = user?.role === "editor" || user?.role === "admin";

  const { data: entries = [], isLoading, refetch } = useQuery({
    queryKey: ["entries", status, q],
    queryFn: async () => (await api.get("/entries", { params: { status, q: q || undefined } })).data,
  });

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const allSelected = entries.length > 0 && entries.every((e) => selected.has(e.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(entries.map((e) => e.id)));
  const clearSel = () => setSelected(new Set());

  const afterMutation = () => {
    clearSel();
    refetch();
    qc.invalidateQueries({ queryKey: ["stats"] });
  };

  const runBulk = async (action) => {
    try {
      const { data } = await api.post("/entries/bulk", { ids: [...selected], action });
      toast.success(action === "delete" ? `Deleted ${data.affected} entr${data.affected === 1 ? "y" : "ies"}` : `Moved ${data.affected} to ${action === "working" ? "Reference DB" : action}`);
      afterMutation();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk action failed");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/entries/export", { params: { status }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `observatory_${status}_export.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Export failed");
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <header className="h-16 flex items-center justify-between px-8 border-b border-white/5 shrink-0">
        <div>
          <h1 className="text-xl font-bold tracking-tight">{title}</h1>
          <p className="text-xs text-slate-500">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <Button data-testid="export-csv" onClick={exportCsv} variant="outline" className="gap-2 rounded-full border-white/10 hover:bg-white/5">
              <DownloadSimple size={16} weight="duotone" /> Export CSV
            </Button>
          )}
          {status === "input" && isEditor && (
            <Button data-testid="list-review-btn" onClick={() => navigate("/review")} className="gap-2 bg-blue-600 hover:bg-blue-500 rounded-full transition-colors duration-200">
              <Gavel size={16} weight="duotone" /> Open Review
            </Button>
          )}
        </div>
      </header>

      <div className="px-8 py-4 border-b border-white/5 shrink-0 flex items-center gap-4">
        <div className="relative max-w-md flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="list-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, notes, keywords…"
            className="pl-9 bg-[#131722] border-white/10" />
        </div>
        {isEditor && entries.length > 0 && (
          <label className="flex items-center gap-2 text-xs text-slate-400 font-mono uppercase tracking-wider cursor-pointer shrink-0">
            <Checkbox data-testid="select-all" checked={allSelected} onCheckedChange={toggleAll} /> Select all
          </label>
        )}
      </div>

      {/* Bulk action bar */}
      {isEditor && selected.size > 0 && (
        <div data-testid="bulk-bar" className="px-8 py-3 border-b border-white/5 bg-blue-500/5 flex items-center gap-3 flex-wrap shrink-0">
          <span className="font-mono text-xs text-blue-300" data-testid="bulk-count">{selected.size} selected</span>
          <div className="h-4 w-px bg-white/10" />
          {ROUTE_ACTIONS.filter((a) => a.key !== status).map((a) => (
            <Button key={a.key} size="sm" variant="outline" data-testid={`bulk-${a.key}`} onClick={() => runBulk(a.key)}
              className="gap-1.5 h-8 rounded-full border-white/10 hover:bg-white/5 text-xs">
              <a.icon size={14} weight="duotone" /> {a.label}
            </Button>
          ))}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="outline" data-testid="bulk-delete" className="gap-1.5 h-8 rounded-full border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs">
                <Trash size={14} weight="duotone" /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#131722] border-white/10 text-slate-200">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {selected.size} entr{selected.size === 1 ? "y" : "ies"}?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-400">This permanently removes the selected entries from all databases. This cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border-white/10 hover:bg-white/5">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="bulk-delete-confirm" onClick={() => runBulk("delete")} className="bg-red-600 hover:bg-red-500">Delete permanently</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button size="sm" variant="ghost" data-testid="bulk-clear" onClick={clearSel} className="gap-1.5 h-8 rounded-full text-slate-400 hover:bg-white/5 text-xs ml-auto">
            <X size={14} /> Clear
          </Button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-8">
        {isLoading ? (
          <p className="text-slate-500 text-sm">Loading…</p>
        ) : entries.length === 0 ? (
          <div className="text-center py-20 text-slate-500">
            <p className="text-sm">No entries here yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map((e, i) => (
              <div key={e.id} data-testid={`entry-row-${e.id}`} style={{ animationDelay: `${i * 40}ms` }}
                onClick={() => setDetailId(e.id)}
                className={`animate-fade-in-up bg-[#131722] border rounded-lg p-5 hover:bg-[#1C2130] transition-colors duration-200 cursor-pointer ${selected.has(e.id) ? "border-blue-500/40" : "border-white/5"}`}>
                <div className="flex items-start gap-4">
                  {isEditor && (
                    <div onClick={(ev) => ev.stopPropagation()} className="pt-1">
                      <Checkbox data-testid={`select-${e.id}`} checked={selected.has(e.id)} onCheckedChange={() => toggle(e.id)} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={e.status} />
                      {e.category && <Tag kind="category">{e.category}</Tag>}
                      {e.entry_type && <Tag kind="type">{e.entry_type}</Tag>}
                      {e.priority && <Tag kind="priority">{e.priority}</Tag>}
                    </div>
                    <h3 className="text-base font-semibold tracking-tight mt-2">{e.title}</h3>
                    {e.notes && <p className="text-sm text-slate-400 mt-1 line-clamp-2">{e.notes}</p>}
                    <div className="flex items-center gap-4 mt-3 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                      {e.has_file && <span className="flex items-center gap-1"><FilePdf size={13} /> PDF</span>}
                      {e.url && <a href={e.url} target="_blank" rel="noreferrer" onClick={(ev) => ev.stopPropagation()} className="flex items-center gap-1 text-blue-400 hover:text-blue-300"><LinkSimple size={13} /> Link</a>}
                      <span>By {e.created_by_name}</span>
                    </div>
                    {e.keywords?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">{e.keywords.map((k) => <Tag key={k} kind="keywords">{k}</Tag>)}</div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <div className="flex items-center gap-3 text-xs font-mono">
                      <span className="flex items-center gap-1 text-emerald-400"><ArrowUp size={13} weight="bold" />{e.vote_up}</span>
                      <span className="flex items-center gap-1 text-red-400"><ArrowDown size={13} weight="bold" />{e.vote_down}</span>
                    </div>
                    {status === "input" && isEditor && (
                      <Button size="sm" variant="outline" data-testid={`entry-review-${e.id}`}
                        onClick={(ev) => { ev.stopPropagation(); navigate("/review", { state: { entryId: e.id } }); }}
                        className="rounded-full border-white/10 hover:bg-white/5 text-xs h-7">Review</Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {detailId && (
        <EntryDetailDialog
          entryId={detailId}
          open={!!detailId}
          onOpenChange={(v) => { if (!v) setDetailId(null); }}
          onMutated={afterMutation}
        />
      )}
    </div>
  );
}
